import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ReactDOM from 'react-dom';
import { useConfirmDialog } from '../components/ConfirmDialog';
import api from '../utils/api';
import './ClipsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const getMediaUrl = (key: string | null | undefined): string | null => {
  if (!key) return null;
  if (key.startsWith('http')) return key;
  return `${BACKEND_URL}/uploads/${key}`;
};

interface ScreenplayElement {
  element_type: string;
  character_name?: string;
  content: string;
  element_order: number;
}

interface Clip {
  id: number;
  video_s3_key: string;
  video_title?: string;
  file_size: number;
  duration?: number;
  uploaded_by: number;
  uploader_name: string;
  created_at: string;
  video_url?: string;
}

interface Comment {
  id: number;
  comment_text: string;
  last_edited_by: number;
  editor_name: string;
  updated_at: string;
}

interface Scene {
  scene_number: number;
  elements: ScreenplayElement[];
  clips: Clip[];
  comment: Comment | null;
}

interface UserWithAccess {
  user_id: number;
  name: string;
  email: string;
  created_at: string;
}

// ─────────────────────────────────────────────────────────────
// Portal Video Modal
// ─────────────────────────────────────────────────────────────

interface PortalMediaModalProps {
  url: string;
  onClose: () => void;
}

const PortalMediaModal: React.FC<PortalMediaModalProps> = ({ url, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleDownload = async () => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'video.mp4';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  return ReactDOM.createPortal(
    <div className="cp-video-overlay" onClick={onClose}>
      <div className="cp-video-inner" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="cp-video-close">✕</button>
        <video controls autoPlay className="cp-video-el">
          <source src={url} type="video/mp4" />
        </video>
        <button onClick={handleDownload} className="cp-video-download">
          ↓ Download
        </button>
      </div>
    </div>,
    document.body
  );
};

// ─────────────────────────────────────────────────────────────
// Scene / Extra Modal
// ─────────────────────────────────────────────────────────────

interface SceneModalProps {
  scene: Scene | null;           // null = extra clips
  extraClips?: Clip[];
  extraComment?: Comment | null;
  hasEditAccess: boolean;
  storyId: string;
  onClose: () => void;
  onRefresh: () => void;
  onOpenVideo: (url: string) => void;
}

const SceneModal: React.FC<SceneModalProps> = ({
  scene,
  extraClips,
  extraComment,
  hasEditAccess,
  storyId,
  onClose,
  onRefresh,
  onOpenVideo,
}) => {
  const { confirm, confirmDialog } = useConfirmDialog();
  const isExtra = scene === null;
  const clips   = isExtra ? (extraClips ?? []) : scene!.clips;
  const comment = isExtra ? (extraComment ?? null) : scene!.comment;

  const [editingComment, setEditingComment] = useState(false);
  const [commentText, setCommentText]       = useState(comment?.comment_text ?? '');
  const [uploading, setUploading]           = useState(false);
  const [error, setError]                   = useState('');
  const [message, setMessage]               = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const flashError   = (msg: string) => { setError(msg);   setTimeout(() => setError(''),   2500); };
  const flashMessage = (msg: string) => { setMessage(msg); setTimeout(() => setMessage(''), 2000); };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/'))    { flashError('Please upload a video file'); return; }
    if (file.size > 500 * 1024 * 1024)      { flashError('Video must be under 500 MB'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('video', file);
      formData.append('videoTitle', file.name);
      if (isExtra) {
        await api.post(`/clips/${storyId}/extra`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post(`/clips/${storyId}/scenes/${scene!.scene_number}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      flashMessage('Video uploaded');
      onRefresh();
    } catch (err: any) {
      flashError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (clipId: number) => {
    const approved = await confirm({
      title: 'Delete Video',
      message: 'Delete this video?',
      confirmLabel: 'Delete Video',
      cancelLabel: 'Keep Video',
      danger: true,
    });
    if (!approved) return;
    try {
      if (isExtra) {
        await api.delete(`/clips/${storyId}/extra/clips/${clipId}`);
      } else {
        await api.delete(`/clips/${storyId}/scenes/clips/${clipId}`);
      }
      flashMessage('Deleted');
      onRefresh();
    } catch (err: any) {
      flashError(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleDownload = async (clip: Clip) => {
    if (!clip.video_url) return;
    try {
      const response = await fetch(clip.video_url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = clip.video_title || `video_${clip.id}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      window.open(clip.video_url, '_blank');
    }
  };

  const handleSaveComment = async () => {
    try {
      if (isExtra) {
        await api.put(`/clips/${storyId}/extra/comment`, { commentText });
      } else {
        await api.put(`/clips/${storyId}/scenes/${scene!.scene_number}/comment`, { commentText });
      }
      flashMessage('Comment saved');
      setEditingComment(false);
      onRefresh();
    } catch (err: any) {
      flashError(err.response?.data?.message || 'Failed to save');
    }
  };

  const renderElements = (elements: ScreenplayElement[]) => (
    <div className="cp-screenplay">
      {elements.map((el, i) => {
        switch (el.element_type) {
          case 'scene_heading':
            return <div key={i} className="cp-el cp-el--heading">{el.content}</div>;
          case 'location':
            return <div key={i} className="cp-el cp-el--location">{el.content}</div>;
          case 'narration':
            return <div key={i} className="cp-el cp-el--narration">{el.content}</div>;
          case 'action':
            return <div key={i} className="cp-el cp-el--action">{el.content}</div>;
          case 'dialogue':
            return (
              <div key={i} className="cp-el cp-el--dialogue">
                <div className="cp-el-char">{el.character_name}</div>
                <div className="cp-el-line">{el.content}</div>
              </div>
            );
          case 'parenthetical':
            return <div key={i} className="cp-el cp-el--paren">{el.content}</div>;
          case 'transition':
            return <div key={i} className="cp-el cp-el--transition">{el.content}</div>;
          default:
            return <div key={i} className="cp-el cp-el--action">{el.content}</div>;
        }
      })}
    </div>
  );

  return ReactDOM.createPortal(
    <div className="cp-scene-overlay" onClick={onClose}>
      <div className="cp-scene-modal" onClick={(e) => e.stopPropagation()}>
        {confirmDialog}

        {/* ── Header ── */}
        <div className="cp-scene-modal-header">
          <div className="cp-scene-modal-title">
            {isExtra ? (
              <>
                <span className="cp-scene-modal-label">Extra</span>
                <span className="cp-scene-modal-name">Unscripted Clips</span>
              </>
            ) : (
              <>
                <span className="cp-scene-modal-label">Scene</span>
                <span className="cp-scene-modal-name">{scene!.scene_number}</span>
              </>
            )}
          </div>
          <button className="cp-scene-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Flash messages ── */}
        {error   && <div className="cp-flash cp-flash--error">{error}</div>}
        {message && <div className="cp-flash cp-flash--ok">{message}</div>}

        <div className="cp-scene-modal-body">

          {/* ── Script column (only for scenes) ── */}
          {!isExtra && scene!.elements.length > 0 && (
            <section className="cp-modal-section">
              <h3 className="cp-modal-section-title">Script</h3>
              <div className="cp-screenplay-wrap">
                {renderElements(scene!.elements)}
              </div>
            </section>
          )}

          {/* ── Videos ── */}
          <section className="cp-modal-section">
            <div className="cp-modal-section-head">
              <h3 className="cp-modal-section-title">
                Videos
                <span className="cp-modal-count">{clips.length}</span>
              </h3>
              {hasEditAccess && (
                <label className="cp-upload-btn">
                  {uploading ? 'Uploading…' : '+ Upload'}
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleUpload}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </div>

            {clips.length === 0 ? (
              <div className="cp-empty">No videos yet.</div>
            ) : (
              <div className="cp-clips-list">
                {clips.map((clip) => (
                  <div
                    key={clip.id}
                    className="cp-clip-row"
                    onClick={() => clip.video_url && onOpenVideo(clip.video_url)}
                    style={{ cursor: clip.video_url ? 'pointer' : 'default' }}
                  >
                    {clip.video_url ? (
                      <div className="cp-clip-thumb">
                        <video preload="metadata" style={{ pointerEvents: 'none' }}>
                          <source src={`${clip.video_url}#t=0.5`} type="video/mp4" />
                        </video>
                        <span className="cp-clip-play">▶</span>
                      </div>
                    ) : (
                      <div className="cp-clip-thumb cp-clip-thumb--empty">—</div>
                    )}
                    <div className="cp-clip-meta">
                      <p className="cp-clip-title">{clip.video_title || 'Untitled'}</p>
                      <p className="cp-clip-sub">
                        {clip.uploader_name} · {new Date(clip.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="cp-clip-btns" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="cp-clip-btn cp-clip-btn--dl"
                        onClick={() => handleDownload(clip)}
                      >↓</button>
                      {hasEditAccess && (
                        <button
                          className="cp-clip-btn cp-clip-btn--del"
                          onClick={() => handleDelete(clip.id)}
                        >✕</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Editor Notes ── */}
          <section className="cp-modal-section">
            <h3 className="cp-modal-section-title">Editor Notes</h3>
            {editingComment ? (
              <div className="cp-comment-edit">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add notes for editors…"
                  className="cp-comment-textarea"
                  rows={4}
                />
                <div className="cp-comment-actions">
                  <button className="cp-btn cp-btn--save" onClick={handleSaveComment}>Save</button>
                  <button className="cp-btn cp-btn--cancel" onClick={() => setEditingComment(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="cp-comment-display">
                {comment ? (
                  <>
                    <p className="cp-comment-text">{comment.comment_text}</p>
                    <p className="cp-comment-meta">
                      {comment.editor_name} · {new Date(comment.updated_at).toLocaleDateString()}
                    </p>
                    {hasEditAccess && (
                      <button
                        className="cp-btn cp-btn--edit"
                        onClick={() => { setCommentText(comment.comment_text); setEditingComment(true); }}
                      >Edit</button>
                    )}
                  </>
                ) : (
                  <>
                    <p className="cp-comment-empty">No notes yet.</p>
                    {hasEditAccess && (
                      <button
                        className="cp-btn cp-btn--edit"
                        onClick={() => { setCommentText(''); setEditingComment(true); }}
                      >Add Notes</button>
                    )}
                  </>
                )}
              </div>
            )}
          </section>

        </div>
      </div>
    </div>,
    document.body
  );
};

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────

const ClipsPage: React.FC = () => {
  const navigate = useNavigate();
  const { storyId } = useParams<{ storyId: string }>();
  const { user } = useAuth();
  const { confirm, confirmDialog } = useConfirmDialog();

  const [scenes, setScenes]             = useState<Scene[]>([]);
  const [extraClips, setExtraClips]     = useState<Clip[]>([]);
  const [extraComment, setExtraComment] = useState<Comment | null>(null);
  const [isOwner, setIsOwner]           = useState(false);
  const [hasEditAccess, setHasEditAccess] = useState(false);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [message, setMessage]           = useState('');

  // Access modal
  const [showAccessModal, setShowAccessModal]   = useState(false);
  const [usersWithAccess, setUsersWithAccess]   = useState<UserWithAccess[]>([]);
  const [groupMembers, setGroupMembers]         = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers]     = useState(false);

  // Scene modal — null = closed, number = scene index, 'extra' = extra tile
  const [openScene, setOpenScene] = useState<number | 'extra' | null>(null);

  // Video lightbox
  const [openVideoUrl, setOpenVideoUrl] = useState<string | null>(null);

  useEffect(() => { loadClips(); }, [storyId]);

  const loadClips = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/clips/${storyId}`);
      setScenes(response.data.scenes);
      setExtraClips(response.data.extraClips);
      setExtraComment(response.data.extraComment);
      setIsOwner(response.data.isOwner);
      setHasEditAccess(response.data.hasEditAccess);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load clips');
    } finally {
      setLoading(false);
    }
  };

  const loadAccessList = async () => {
    try {
      setLoadingMembers(true);
      const [accessRes, groupRes] = await Promise.all([
        api.get(`/clips/${storyId}/access`),
        api.get(`/groups/${storyId}/members`),
      ]);
      setUsersWithAccess(accessRes.data.usersWithAccess);
      setGroupMembers(groupRes.data.members || []);
      setShowAccessModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load access list');
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleGrantAccess = async (userId: number) => {
    try {
      await api.post(`/clips/${storyId}/access`, { targetUserId: userId });
      setMessage('Access granted');
      setTimeout(() => setMessage(''), 2000);
      loadAccessList();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to grant access');
    }
  };

  const handleRevokeAccess = async (userId: number) => {
    const approved = await confirm({
      title: 'Revoke Access',
      message: 'Revoke access for this user?',
      confirmLabel: 'Revoke Access',
      cancelLabel: 'Keep Access',
      danger: true,
    });
    if (!approved) return;
    try {
      await api.delete(`/clips/${storyId}/access/${userId}`);
      setMessage('Access revoked');
      setTimeout(() => setMessage(''), 2000);
      loadAccessList();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to revoke access');
    }
  };

  // Current scene object for the open modal
  const activeScene: Scene | null =
    openScene !== null && openScene !== 'extra'
      ? scenes.find(s => s.scene_number === openScene) ?? null
      : null;

  if (loading) {
    return (
      <div className="cp-container">
        <div className="cp-loading">Loading clips…</div>
      </div>
    );
  }

  return (
    <div className="cp-container">
      {confirmDialog}

      {/* ── Header ── */}
      <div className="cp-header">
        <button onClick={() => navigate(`/group/${storyId}`)} className="cp-back-btn">
          ← Back to Group
        </button>
        <h1 className="cp-title">Clips</h1>
        {isOwner && (
          <button onClick={loadAccessList} className="cp-access-btn">
            Access to upload
          </button>
        )}
      </div>

      {/* ── Flash ── */}
      {error   && <div className="cp-page-flash cp-page-flash--error">{error}</div>}
      {message && <div className="cp-page-flash cp-page-flash--ok">{message}</div>}

      {/* ── Access badge ── */}
      <div className="cp-access-badge">
        {hasEditAccess
          ? 'Upload & edit access'
          : 'View-only access — you can still download videos'
        }
      </div>

      {/* ── Tile grid ── */}
      <div className="cp-tile-grid">

        {scenes.map((scene) => {
          const firstClip = scene.clips[0];
          return (
            <button
              key={scene.scene_number}
              className="cp-tile"
              onClick={() => setOpenScene(scene.scene_number)}
            >
              <div className="cp-tile-thumb">
                {firstClip?.video_url ? (
                  <video preload="metadata" style={{ pointerEvents: 'none' }}>
                    <source src={`${firstClip.video_url}#t=0.5`} type="video/mp4" />
                  </video>
                ) : (
                  <div className="cp-tile-thumb-empty">
                    <span className="cp-tile-thumb-icon">▶</span>
                  </div>
                )}
                {scene.clips.length > 0 && (
                  <span className="cp-tile-count">{scene.clips.length}</span>
                )}
              </div>

              <div className="cp-tile-info">
                <span className="cp-tile-label">Scene</span>
                <span className="cp-tile-num">{scene.scene_number}</span>
              </div>

              {scene.elements.length > 0 && (
                <p className="cp-tile-preview">
                  {scene.elements[0].content.slice(0, 60)}
                  {scene.elements[0].content.length > 60 ? '…' : ''}
                </p>
              )}

              {scene.comment && (
                <div className="cp-tile-note-dot" title="Has editor notes" />
              )}
            </button>
          );
        })}

        <button
          className="cp-tile cp-tile--extra"
          onClick={() => setOpenScene('extra')}
        >
          <div className="cp-tile-thumb cp-tile-thumb--extra">
            {extraClips[0]?.video_url ? (
              <video preload="metadata" style={{ pointerEvents: 'none' }}>
                <source src={`${extraClips[0].video_url}#t=0.5`} type="video/mp4" />
              </video>
            ) : (
              <div className="cp-tile-thumb-empty">
                <span className="cp-tile-thumb-icon">Extra</span>
              </div>
            )}
            {extraClips.length > 0 && (
              <span className="cp-tile-count">{extraClips.length}</span>
            )}
          </div>
          <div className="cp-tile-info">
            <span className="cp-tile-label">Extra</span>
            <span className="cp-tile-num">Clips</span>
          </div>
          <p className="cp-tile-preview">Videos not tied to specific scenes</p>
          {extraComment && (
            <div className="cp-tile-note-dot" title="Has editor notes" />
          )}
        </button>

      </div>

      {/* ── Access Management Modal ── */}
      {showAccessModal && (
        <div className="cp-scene-overlay" onClick={() => setShowAccessModal(false)}>
          <div className="cp-scene-modal cp-scene-modal--access" onClick={(e) => e.stopPropagation()}>
            <div className="cp-scene-modal-header">
              <div className="cp-scene-modal-title">
                <span className="cp-scene-modal-label">Manage</span>
                <span className="cp-scene-modal-name">Access</span>
              </div>
              <button className="cp-scene-modal-close" onClick={() => setShowAccessModal(false)}>✕</button>
            </div>
            <p className="cp-access-modal-hint">
              Grant members permission to upload videos and edit editor notes.
            </p>
            <div className="cp-scene-modal-body">
              <section className="cp-modal-section">
                {loadingMembers ? (
                  <div className="cp-empty">Loading members…</div>
                ) : groupMembers.length === 0 ? (
                  <div className="cp-empty">No members found.</div>
                ) : (
                  <div className="cp-access-list">
                    {groupMembers.map((member) => {
                      const dpUrl = getMediaUrl(member.profile_picture);
                      const hasAccess = usersWithAccess.some(u => u.user_id === member.user_id);
                      return (
                        <div key={member.user_id} className="cp-access-row">
                          <div className="cp-access-avatar">
                            {dpUrl ? (
                              <img src={dpUrl} alt={member.name}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            ) : (
                              member.name?.charAt(0).toUpperCase() ?? '?'
                            )}
                          </div>
                          <div className="cp-access-info">
                            <p className="cp-access-name">{member.name || 'Unknown'}</p>
                            <p className="cp-access-email">{member.email || ''}</p>
                          </div>
                          {hasAccess ? (
                            <button
                              className="cp-btn cp-btn--revoke"
                              onClick={() => handleRevokeAccess(member.user_id)}
                            >Revoke</button>
                          ) : (
                            <button
                              className="cp-btn cp-btn--grant"
                              onClick={() => handleGrantAccess(member.user_id)}
                            >Grant</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}

      {/* ── Scene / Extra Modal ── */}
      {openScene !== null && (
        <SceneModal
          scene={openScene === 'extra' ? null : activeScene}
          extraClips={openScene === 'extra' ? extraClips : undefined}
          extraComment={openScene === 'extra' ? extraComment : undefined}
          hasEditAccess={hasEditAccess}
          storyId={storyId!}
          onClose={() => setOpenScene(null)}
          onRefresh={loadClips}
          onOpenVideo={setOpenVideoUrl}
        />
      )}

      {/* ── Video Lightbox ── */}
      {openVideoUrl && (
        <PortalMediaModal url={openVideoUrl} onClose={() => setOpenVideoUrl(null)} />
      )}

    </div>
  );
};

export default ClipsPage;
